import {StyleSheet} from "aphrodite";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    borderRadius,
    mediaQueries,
    standardTransition,
    standardTransitionExtras,
    standardTransitionMs,
} from "@khan/shared-styles";

import styleConstants from "./style-constants.ts";

// Field requirement and error text on input fields have this line height.
const FIELD_REQ_LINE_HEIGHT = 18;
// Max left- and right-padding of text at xl sizes.
const XL_MAX_WIDTH =
    styleConstants.MAX_SCREEN_WIDTH - 2 * styleConstants.XL_MAX_H_PADDING;
// Max width of text in lg sizes.
const LG_MAX_WIDTH = 354;

// Horizontal padding to be applied to all text input fields in the
// sign up and log in forms
const TEXT_INPUT_H_PADDING = 15;

export default StyleSheet.create({
    fieldWrapper: {
        display: "flex",
        width: "100%",

        ":not(:first-of-type)": {
            marginTop: 2,
        },
    },
    label: {
        display: "block",
        fontSize: 15,
        fontWeight: 600,
        lineHeight: "23px",
        color: color.offBlack,
        width: "100%",

        ":not(:last-of-type)": {
            marginRight: 8,
        },
    },
    link: {
        color: color.green,
        ":hover": {
            textDecoration: "none",
        },
    },
    errorInput: {
        borderColor: color.red,
    },
    errorInputShake: {
        // @ts-expect-error - TS2322 - Type '{ from: { transform: string; }; to: { transform: string; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            from: {
                transform: "translate(-5px)",
            },
            to: {
                transform: "translate(5px)",
            },
        },
        animationTimingFunction: standardTransitionExtras,
        animationDuration: `${standardTransitionMs}ms`,
        animationIterationCount: "3",
        animationDirection: "alternate",

        ":focus": {
            borderColor: color.red,
        },
    },
    fieldRequirement: {
        fontSize: 12,
        color: color.offBlack,
        fontWeight: "normal",
        lineHeight: `${FIELD_REQ_LINE_HEIGHT}px`,
        marginTop: 4,
        marginBottom: 4,
    },
    fieldRequirementContainer: {
        display: "flex",
        flexDirection: "column",
        minHeight: 30,
        boxSizing: "border-box",

        ":last-child": {
            marginBottom: 0,
        },
    },
    fieldWarning: {
        color: color.offBlack,
        opacity: 1,
        height: styleConstants.FIELD_REQ_HEIGHT,
        transition: standardTransition,
        transitionProperty: `opacity`,
    },
    fieldError: {
        color: color.red,
        lineHeight: `${styleConstants.FIELD_REQ_HEIGHT}px`,
        // We don't anticipate even the longest field error string to exceed
        // 4 lines (even at 320px wide screen sizes). Setting a max-height
        // allows us to have smooth height transitions, which is currently
        // (Sept 2017) not possible with setting a min-height.
        maxHeight: `calc(4 * ${FIELD_REQ_LINE_HEIGHT}px)`,
        transition: standardTransition,
        transitionProperty: `max-height, opacity`,
    },
    hiddenFieldError: {
        opacity: 0,
        height: styleConstants.FIELD_REQ_HEIGHT,
        maxHeight: styleConstants.FIELD_REQ_HEIGHT,
    },
    mainHeading: {
        color: color.white,
        maxWidth: XL_MAX_WIDTH,
        fontSize: styleConstants.HEADING_FONT_SIZE_XL,
        fontWeight: "bold",
        lineHeight: 1.1,
        marginTop: styleConstants.XL_MARGIN_TOP,
        marginBottom: 0,

        [mediaQueries.lg]: {
            maxWidth: LG_MAX_WIDTH,
            fontSize: styleConstants.HEADING_FONT_SIZE,
            lineHeight: 1.25,
            marginTop: styleConstants.LG_MARGIN_TOP,
            marginLeft: styleConstants.LG_MIN_H_PADDING,
            marginRight: styleConstants.LG_MIN_H_PADDING,
        },

        [mediaQueries.mdOrSmaller]: {
            width: styleConstants.FORM_WIDTH,
            marginTop: 0,
            marginBottom: 0,
            fontSize: styleConstants.HEADING_FONT_SIZE,
            lineHeight: "27px",
        },

        [mediaQueries.xs]: {
            width: "100%",
        },
    },
    description: {
        color: color.white,
        maxWidth: XL_MAX_WIDTH,
        fontWeight: 400,
        // Disable subpixel antialiasing for OSX (improves light on dark text
        // that are intended to be lighter weight.)
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        marginTop: 16,
        marginBottom: 0,

        [mediaQueries.lg]: {
            marginLeft: styleConstants.LG_MIN_H_PADDING,
            marginRight: styleConstants.LG_MIN_H_PADDING,
            marginBottom: 0,

            ":first-child": {
                marginTop: 16,
            },

            ":last-child": {
                // Add some vertical space after to the screen after the last
                // subheading. Useful for enlongating the screen for short
                // viewports.
                marginBottom: 96,
            },
        },

        [mediaQueries.mdOrSmaller]: {
            width: styleConstants.FORM_WIDTH,
            marginTop: 16,
        },

        [mediaQueries.xs]: {
            width: "100%",
        },
    },
    subHeadingText: {
        fontSize: styleConstants.SUBHEADING_FONT_SIZE,
        lineHeight: 1.47,

        [mediaQueries.mdOrSmaller]: {
            display: "none",
        },
    },
    signupButtonWrapper: {
        marginTop: 4,
    },
    resetSize: {
        fontSize: "inherit",
    },
    rule: {
        border: 0,
        background: "none",
        marginTop: 36,
        marginBottom: 36,
    },
    textInput: {
        marginTop: 4,
        padding: `0 ${TEXT_INPUT_H_PADDING}px`,
        height: 40,
        fontSize: styleConstants.BODY_FONT_SIZE,
        border: `1px solid ${color.offBlack16}`,
        borderRadius: borderRadius,
        // Remove left/right padding
        width: `
            calc(100% - ${TEXT_INPUT_H_PADDING}px - ${TEXT_INPUT_H_PADDING}px)
        `,
        outline: "none",
        flex: 1,

        ":focus": {
            borderColor: color.green,
        },
    },
});
